import { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import withStyles from 'beam/dist/styles'
import { withTranslation } from 'react-i18next'
import classNames from 'classnames'
import TranscriptionSearch from './TranscriptionSearch'
import Typography from 'beam/dist/Typography'
import { CircularProgress } from 'beam/dist/Progress'
import TranscriptionBody from './TranscriptionBody'

const styles = () => ({
  root: {
    display: 'inline-flex',
    flexDirection: 'column',
    height: '100%',
  },
  rootNoBubbles: {
    justifyContent: 'center',
    alignItems: 'center',
    flexGrow: 1,
  },
})

const CLUMP_TYPE = {
  HIT: 'HIT',
  QUERY: 'QUERY',
  NONE: 'NONE',
}
class TranscriptionContent extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    classes: PropTypes.object.isRequired,
    /** if the transcription data is still being loaded */
    transcriptionLoading: PropTypes.bool.isRequired,
    /** if the transcription data or the storage file is still being loaded */
    loading: PropTypes.bool.isRequired,
    /** if loading the transcription data failed for some reason */
    error: PropTypes.bool.isRequired,
    isRootRecording: PropTypes.bool,
    cache: PropTypes.object,
    /** Time to seek the video to in milliseconds */
    currentTime: PropTypes.number.isRequired,
    /** Callback for time updates */
    setTime: PropTypes.func.isRequired,
    /** audio offset */
    audioOffset: PropTypes.number.isRequired,
    /** Callback to load transcription data */
    requestTranscription: PropTypes.func.isRequired,
    /** id of call to load */
    ccrid: PropTypes.number,
    /** Sys config for silence event */
    silenceEventDuration: PropTypes.number.isRequired,
    isSearchOpen: PropTypes.bool,
    printRef: PropTypes.oneOfType([
      // Either a function
      PropTypes.func,
      // Or the instance of a DOM native element (see the note about SSR)
      PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
    ]),
    /** Result from the API */
    texts: PropTypes.arrayOf(
      PropTypes.shape({
        channel: PropTypes.number.isRequired,
        end: PropTypes.number.isRequired,
        hits: PropTypes.array.isRequired,
        start: PropTypes.number.isRequired,
        sentiment: PropTypes.number,
        text: PropTypes.string.isRequired,
        words: PropTypes.arrayOf(
          PropTypes.shape({
            hit: PropTypes.bool.isRequired,
            start: PropTypes.number.isRequired,
            word: PropTypes.string.isRequired,
          }),
        ),
      }),
    ),
  }

  state = {
    query: '',
    queryTotalHits: 0,
    queryCurrentIndex: 0,
    bubbles: [],
  }

  componentDidMount() {
    this.requestTranscription()
    this.searchFor({ target: { value: '' } })
  }

  componentDidUpdate(prevProps) {
    const { ccrid, texts, audioOffset, transcriptionLoading } = this.props

    if (prevProps.ccrid !== ccrid) {
      this.requestTranscription()
    } else if (!transcriptionLoading && (prevProps.texts !== texts || prevProps.audioOffset !== audioOffset)) {
      this.searchFor({ target: { value: this.state.query } })
    }
  }

  requestTranscription() {
    const { requestTranscription, ccrid, isRootRecording, cache } = this.props
    requestTranscription(ccrid, isRootRecording, cache)
  }

  goToQueryHit = direction => {
    let nextIndex = this.state.queryCurrentIndex + direction
    if (nextIndex < 1) {
      nextIndex = this.state.queryTotalHits
    } else if (nextIndex > this.state.queryTotalHits) {
      nextIndex = 1
    }
    this.setState({
      queryCurrentIndex: nextIndex,
    })
  }

  searchFor = ({ target: { value: query } }) => {
    let queryTotalHits = 0
    let hitCount = 0
    const { texts, audioOffset } = this.props

    //Get beginning and end index of each hit and query hit
    const bubbles = texts.map(statement => {
      if (query !== '') {
        queryTotalHits += statement.text.toLowerCase().split(query.toLowerCase()).length - 1
      }

      const textToClumpsMap = Array(statement.text.length).fill(-1)
      //For each word, we want to map which indicies of the text belongs to that word
      const textToWordsMap = [...textToClumpsMap]
      let textIndex = 0
      statement.words.forEach(({ word }, wordIndex) => {
        for (let i = 0; i < word.length; i++) {
          textToWordsMap[textIndex] = wordIndex
          textIndex++
        }
        //Account for the space between each word
        textIndex++
      })

      return {
        start: statement.start + audioOffset,
        end: statement.end + audioOffset,
        sentiment: statement.sentiment,
        channel: statement.channel,
        clumps: (({ text, hits, words }) => {
          //Sort by shortest first
          const sortedTranscriptionHits = [...hits]
            .sort((a, b) => -1 * (a.text.end - a.text.start) - (b.text.end - b.text.start))
            .map(hit => ({ ...hit, type: CLUMP_TYPE.HIT }))

          //Next do search
          //Use the word map to determine the start
          //Format like a transcription hit
          // {
          //   "text": {
          //     "start": 176,
          //     "end": 185
          //   },
          //   "audio": {
          //     "start": 139590,
          //     "end": 140080
          //   },
          //  ...
          // }
          const queryHits = []
          if (query !== '') {
            let lastQueryIndex = 0
            while (text.toLowerCase().indexOf(query.toLowerCase(), lastQueryIndex) !== -1) {
              const queryIndex = text.toLowerCase().indexOf(query.toLowerCase(), lastQueryIndex)
              hitCount++

              //If the query has an initial space, use next index, which should be a word because there is only one space between each word
              const firstWordIndex =
                textToWordsMap[queryIndex] === -1 ? textToWordsMap[queryIndex + 1] : textToWordsMap[queryIndex]
              const firstWord = words[firstWordIndex]
              queryHits.push({
                type: CLUMP_TYPE.QUERY,
                words: query,
                text: {
                  start: queryIndex,
                  end: queryIndex + query.length - 1,
                },
                audio: {
                  start: firstWord.start + audioOffset,
                  end: firstWord.start + audioOffset, //We don't actually know the end
                },
                qHit: true,
                qIndex: hitCount,
              })
              lastQueryIndex = queryIndex + query.length
            }
          }

          //Combine transcription and query hits, query hits are last, so they overwrite everything
          const sortedClumps = [...sortedTranscriptionHits, ...queryHits]

          //Map each hit onto the textToClumpsMap (longest will naturally overwrite shortest)
          sortedClumps.forEach(({ text: { start, end } }, sortedClumpIndex) => {
            for (let i = start; i <= end; i++) {
              textToClumpsMap[i] = sortedClumpIndex
            }
          })

          const clumps = []
          let currentIndex = 0
          //Iterate through the entire text
          while (currentIndex < text.length) {
            //Set the current clump
            const currentClumpIndex = textToClumpsMap[currentIndex]
            const currentClump = sortedClumps[currentClumpIndex]
            const clumpStartIndex = currentIndex

            //While we are on the same clump, increase the index and clump length
            while (textToClumpsMap[currentIndex] === currentClumpIndex) {
              currentIndex++
            }

            //Now we are at the beginning of the next clump
            //Create the clump
            clumps.push({
              words: text.substring(clumpStartIndex, currentIndex), //MDN: substring excludes the currentIndex but includes all before
              start:
                //We don't care about non hit or search clumps audio start times
                currentClump && [CLUMP_TYPE.HIT, CLUMP_TYPE.QUERY].includes(currentClump.type)
                  ? currentClump.audio.start + audioOffset
                  : audioOffset, //Audio offset
              hit: !!(currentClump && currentClump.type === CLUMP_TYPE.HIT),
              qHit: !!(currentClump && currentClump.type === CLUMP_TYPE.QUERY),
              qIndex: currentClump && currentClump.type === CLUMP_TYPE.QUERY ? currentClump.qIndex : -1,
            })
          }

          return clumps
        })(statement),
      }
    })

    const queryCurrentIndex = queryTotalHits !== 0 ? 1 : 0

    this.setState({
      query,
      queryTotalHits,
      queryCurrentIndex,
      bubbles,
    })
  }

  closeSearch() {
    this.setState({ isSearchOpen: false })
  }

  renderLoading() {
    return <CircularProgress />
  }

  renderError() {
    const { t } = this.props
    return (
      <div>
        <Typography color="secondary" align="center">
          {t('error')}
        </Typography>
        <Typography color="secondary" align="center">
          {t('transcriptionRetrievalError')}
        </Typography>
      </div>
    )
  }

  renderNoData() {
    const { t } = this.props
    return <Typography>{t('noTranscription')}</Typography>
  }

  renderData() {
    const {
      currentTime,
      setTime,
      silenceEventDuration,
      audioOffset,
      printRef,
      isSearchOpen,
    } = this.props
    const { query, queryCurrentIndex, queryTotalHits, bubbles } = this.state

    return (
      <Fragment>
        <TranscriptionSearch
          searchFor={this.searchFor}
          query={query}
          queryCurrentIndex={queryCurrentIndex}
          queryTotalHits={queryTotalHits}
          goToQueryHit={this.goToQueryHit}
          open={isSearchOpen}
          onClose={this.closeSearch.bind(this)}
        />
        <TranscriptionBody
          ref={printRef}
          currentTime={currentTime}
          setTime={setTime}
          audioOffset={audioOffset}
          silenceEventDuration={silenceEventDuration}
          queryCurrentIndex={queryCurrentIndex}
          bubbles={bubbles}
        />
      </Fragment>
    )
  }

  render() {
    const { classes, loading, error, texts } = this.props
    const noBubbles = loading || error || texts.length === 0

    return (
      <div className={classNames(classes.root, noBubbles && classes.rootNoBubbles)}>
        {(loading && this.renderLoading()) ||
          (error && this.renderError()) ||
          (texts.length === 0 && this.renderNoData()) ||
          this.renderData()}
      </div>
    )
  }
}

export { TranscriptionContent as UnstyledTranscriptionContent }
export default withTranslation()(withStyles(styles)(TranscriptionContent))
